import { styled } from '@linaria/react';
import { Flex, Group } from '@mantine/core';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { DeliveryImpactResponse } from '../../../api/agent-miner-client/agent-miner-client.type';
import { baseWhite, inkLight, secondaryBase, skyDark, skyLight } from '../../../styles/design-tokens';
import { Icon } from '../../../ui-library/icon/icon';
import { H3, Text } from '../../../ui-library/typography/typography';
import { StyledButton } from '../../financials/charts/shared/chart-display-type-button-set';
import { LineChartTooltip } from '../../process-analysis/components/line-chart/line-chart-tooltip';
import { useDeliveryTrendView } from './delivery-impact.hooks';
import { AI_SERIES_KEY, BASELINE_SERIES_KEY, buildDeliveryTrendData } from './delivery-impact.helpers';

type Props = {
  response: DeliveryImpactResponse;
};

const formatCycleTime = (value: number | null) => (value !== null && value !== undefined ? `${value}d` : '—');

/**
 * Median task cycle time per period, AI-assisted (solid accent line) vs
 * baseline (dashed grey line). Periods without completions are bridged
 * (connectNulls) so each line stays continuous; the period size follows the
 * sidebar's time-allocation filter like the Model Comparison chart.
 *
 * @param {Props} props - Component props
 * @returns {JSX.Element} The chart card content
 */
export const DeliveryTrendChart = ({ response }: Props) => {
  const { t } = useTranslation();
  const { trendView, setTrendView } = useDeliveryTrendView();
  const chartData = useMemo(() => buildDeliveryTrendData(response), [response]);

  return (
    <ChartLayout data-testid="delivery-impact-trend-chart">
      <Header>
        <TitleBlock>
          <H3>{t('agent_miner.delivery_impact.trend_title')}</H3>
          <Text size="small" color={inkLight}>
            {t('agent_miner.delivery_impact.trend_subtitle')}
          </Text>
        </TitleBlock>
        <Group gap="xs" data-testid="delivery-impact-chart-toggle">
          <StyledButton
            label={t('agent_miner.delivery_impact.toggle_line')}
            iconName="stacked_line_chart"
            selected={trendView === 'line'}
            onClick={() => setTrendView('line')}
          />
          <StyledButton
            label={t('agent_miner.delivery_impact.toggle_bar')}
            iconName="bar_chart"
            selected={trendView === 'bar'}
            onClick={() => setTrendView('bar')}
          />
        </Group>
      </Header>
      <ChartArea>
        <ResponsiveContainer width="100%" height="100%" minHeight={160}>
          <ComposedChart data={chartData} margin={{ top: 20, right: 10, bottom: 24 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={skyLight} />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 12 }}
              axisLine={{ stroke: skyLight }}
              tickLine={{ stroke: skyLight }}
            />
            <YAxis
              tick={{ fontSize: 12 }}
              axisLine={{ stroke: skyLight }}
              tickLine={{ stroke: skyLight }}
              label={{
                value: t('agent_miner.delivery_impact.trend_axis_label'),
                angle: -90,
                position: 'insideLeft',
                style: { textAnchor: 'middle' },
                offset: 10,
              }}
            />
            <RechartsTooltip content={<LineChartTooltip formatValue={formatCycleTime} />} />
            {trendView === 'bar' ? (
              <>
                <Bar
                  dataKey={AI_SERIES_KEY}
                  name={t('agent_miner.delivery_impact.ai_label')}
                  fill={secondaryBase}
                  isAnimationActive={false}
                />
                <Bar
                  dataKey={BASELINE_SERIES_KEY}
                  name={t('agent_miner.delivery_impact.baseline_label')}
                  fill={skyDark}
                  isAnimationActive={false}
                />
              </>
            ) : (
              <>
                <Line
                  type="monotone"
                  dataKey={AI_SERIES_KEY}
                  name={t('agent_miner.delivery_impact.ai_label')}
                  stroke={secondaryBase}
                  strokeWidth={2.5}
                  isAnimationActive={false}
                  connectNulls
                />
                <Line
                  type="monotone"
                  dataKey={BASELINE_SERIES_KEY}
                  name={t('agent_miner.delivery_impact.baseline_label')}
                  stroke={skyDark}
                  strokeWidth={2}
                  strokeDasharray="6 4"
                  isAnimationActive={false}
                  connectNulls
                />
              </>
            )}
            <Legend content={<DeliveryTrendLegend />} verticalAlign="bottom" />
          </ComposedChart>
        </ResponsiveContainer>
      </ChartArea>
    </ChartLayout>
  );
};

const ChartLayout = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 420px;
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 12px;
`;

const TitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const DeliveryTrendLegend = () => {
  const { t } = useTranslation();

  return (
    <Flex justify="center" align="center" gap="md" h="100%">
      <LegendItem $color={secondaryBase}>
        <LegendMark $color={secondaryBase}>
          <Icon name="check" size={10} color={baseWhite} />
        </LegendMark>
        <Text size="small" lineHeight="none">
          {t('agent_miner.delivery_impact.ai_label')}
        </Text>
      </LegendItem>
      <LegendItem $color={skyDark}>
        <LegendMark $color={skyDark}>
          <Icon name="check" size={10} color={baseWhite} />
        </LegendMark>
        <Text size="small" lineHeight="none">
          {t('agent_miner.delivery_impact.baseline_label')}
        </Text>
      </LegendItem>
    </Flex>
  );
};

const LegendItem = styled.div<{ $color: string }>`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const LegendMark = styled.span<{ $color: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  border-radius: 3px;
  border: 1.5px solid ${({ $color }) => $color};
  background-color: ${({ $color }) => $color};
  flex-shrink: 0;
`;

/* The chart area must own the remaining vertical space or the chart collapses
   to the ResponsiveContainer's 160px floor. */
const ChartArea = styled.div`
  flex: 1;
  min-height: 0;
`;
